import { useForm } from "react-hook-form";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DatosGenerales, UpdateGeneralInfoRequest } from "../../../../../types/empresa";
import { getErrorMessage } from "../../../../../utils/errorHelpers";
import { ErrorMessage } from "../../../../../components/errors/ErrorMessage";
import { SwitchInput } from "../../../../../components/ui/SwitchInput";
import { useUpdateGeneralInfoMutation } from "../../../empresaApi";
import { CircularProgress } from "@mui/joy";
import { useGetFrecuenciaFilterDataQuery } from "../../../../../api/filter/dataFilterApi";
import { AvailableEmpresaFilters } from "src/utils/enums";
import { ActionButtons } from "../../../../../components/ui/ActionButtons";
import { InlineFormInput } from "src/components/ui/inputs/FormInput";
import { InlineFormSelect } from "src/components/ui/inputs/FormSelect";
import { Groups2Rounded as Groups2RoundedIcon } from '@mui/icons-material';
import { HomeRounded as HomeRoundedIcon } from '@mui/icons-material';
import { NumbersRounded as NumbersRoundedIcon } from '@mui/icons-material';
import { SignpostRounded as SignpostRoundedIcon } from '@mui/icons-material';
import { FmdGoodRounded as FmdGoodRoundedIcon } from '@mui/icons-material';
import { DialpadRounded as DialpadRoundedIcon } from '@mui/icons-material';
import { AccountBalanceRounded as AccountBalanceRoundedIcon } from '@mui/icons-material';
import { BusinessRounded as BusinessRoundedIcon } from '@mui/icons-material';
import { HomeWorkRounded as HomeWorkRoundedIcon } from '@mui/icons-material';
import { TurnLeftRounded as TurnLeftRoundedIcon } from '@mui/icons-material';
import { UpdateRounded as UpdateRoundedIcon } from '@mui/icons-material';
import { ToggleOnRounded as ToggleOnRoundedIcon } from '@mui/icons-material';

interface Props {
    empresaId: number;
    datosGenerales: DatosGenerales;
    setShowEdit: Dispatch<SetStateAction<boolean>>;
}

export function EditDatosGenerales({ datosGenerales, setShowEdit, empresaId }: Props) {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [updateGeneralInfo, { isLoading }] = useUpdateGeneralInfoMutation();
    const [formatoMeses, setFormatoMeses] = useState<boolean>(datosGenerales.formatoMeses);
    const [frecuenciaTypeInput, setFrecuenciaTypeInput] = useState<"date" | "text">("text")
    const { data: filters, isLoading: filterLoading } = useGetFrecuenciaFilterDataQuery([
        AvailableEmpresaFilters.Estados,
        AvailableEmpresaFilters.Frecuencias,
        AvailableEmpresaFilters.Municipios,
    ]);

    const iconSize = 20;

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isDirty,
        }
    } = useForm<DatosGenerales>({
        defaultValues: datosGenerales
    });

    const submitUpdate = handleSubmit(async (data: DatosGenerales) => {
        try {
            setErrorMessage(null);

            const request: UpdateGeneralInfoRequest = {
                generalInfo: data,
                empresaId: empresaId 
            };

            await updateGeneralInfo(request).unwrap();

            setShowEdit(false);
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    });

    useEffect(() => {
        const typeInput = formatoMeses ? "text" : "date";
        setFrecuenciaTypeInput(typeInput);
        setValue("formatoMeses", formatoMeses);
    }, [formatoMeses]);

    if(filterLoading)
        return (
        <div className="flex justify-center items-center">
            <CircularProgress />
        </div>);

    return (
        <div className="w-full h-full min-h-full">
            <form onSubmit={submitUpdate} autoComplete="off" className="flex flex-col gap-y-[11px]">

                <InlineFormInput
                    {...register("nombre", {
                        required: "El nombre de la empresa es requerido",
                        maxLength: 40
                    })}
                    label="Nombre:"
                    icon={<BusinessRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.nombre?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.nombre.message}</p>
                )}
                {errors.nombre?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 40</p>
                )}

                <InlineFormInput
                    {...register("sucursal", {
                        maxLength: 40
                    })}
                    label="Sucursal:"
                    icon={<HomeWorkRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.sucursal?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 40</p>
                )}

                <InlineFormInput
                    {...register("numeroTrabajadores", {
                        required: "El número de trabajadores es requerido"
                    })}
                    label="No. Trabajadores:"
                    icon={<Groups2RoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    type="number"/>

                {errors.numeroTrabajadores?.type === "required" && (
                    <p className="valErrorText text-sm">{errors.numeroTrabajadores.message}</p>
                )}

                <InlineFormSelect
                    {...register("estadoId", {
                        required: "El estado es requerida",
                        minLength: 1,
                    })}
                    label="Estado"
                    options={filters?.estadosEmpresa.map((value) => ({ label: value.nombre, value: value.id })) ?? []}
                    icon={<ToggleOnRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {formatoMeses
                ?
                <InlineFormSelect
                    {...register("frecuenciaVisitas", {
                        required: "La frecuencia de visita es requerida",
                        minLength: 1,
                    })}
                    label="Frecuencia de visitas"
                    options={filters?.frecuencia.map((value) => ({ label: value, value: value })) ?? []}
                    icon={<UpdateRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>
                :
                <InlineFormInput
                    {...register("frecuenciaVisitas", {
                        required: "La frecuencia de visita es requerida",
                        minLength: 1,
                    })}
                    label="Frecuencia de visitas"
                    icon={<UpdateRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}
                    type={frecuenciaTypeInput}/>
                }

                <div className="flex items-center gap-x-2 w-full">
                    <div className="rotate-180 flex items-center">
                        <TurnLeftRoundedIcon sx={{ height: iconSize, width: iconSize }}/>
                    </div>
                    <label className="font-semibold min-w-[150px]">Formato de frecuencia</label>
                    <SwitchInput
                        setFormatoMeses={setFormatoMeses}
                        formatoMeses={formatoMeses}/>
                </div>

                <InlineFormInput
                    {...register("calle", {
                        maxLength: 50
                    })}
                    label="Calle:"
                    icon={<HomeRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.calle?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                <InlineFormInput
                    {...register("colonia", {
                        maxLength: 50
                    })}
                    label="Colonia:"
                    icon={<SignpostRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.colonia?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                <InlineFormInput
                    {...register("numeroExterior", {
                        maxLength: 10
                    })}
                    label="No. Exterior:"
                    icon={<NumbersRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.numeroExterior?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 10</p>
                )}

                <InlineFormInput
                    {...register("codigoPostal", {
                        maxLength: 10
                    })}
                    label="Codigo Postal:"
                    icon={<DialpadRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.codigoPostal?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 10</p>
                )}

                <InlineFormSelect
                    {...register("municipioId", {
                        min: 1
                    })}
                    label="Municipio:"
                    options={filters?.municipios.map((value) => ({ label: value.nombre, value: value.id })) ?? []}
                    icon={<AccountBalanceRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.municipioId?.type === "min" && (
                    <p className="valErrorText text-sm">Debe seleccionar un municipio</p>
                )}

                <InlineFormInput
                    {...register("coordenadas", {
                        maxLength: 50
                    })}
                    label="Coordenadas:"
                    icon={<FmdGoodRoundedIcon sx={{ height: iconSize, width: iconSize }}/>}/>

                {errors.coordenadas?.type === "maxLength" && (
                    <p className="valErrorText text-sm">Número máximo de caracteres es 50</p>
                )}

                {errorMessage && (
                    <div className="my-2">
                        <ErrorMessage
                        customMessage>
                            <p className="text-sm">{errorMessage}</p>
                        </ErrorMessage>
                    </div>
                )}

                <ActionButtons 
                    isLoading={isLoading}
                    disabledSaveButton={!isDirty}
                    saveButtonText="Actualizar"
                    onCancel={()=> {
                        setShowEdit(false);
                    }}
                    />
            </form>
        </div>
    );
}
